import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiGet, apiPost, apiDelete, apiPut, getToken } from '@/utils/api';
import { toast } from 'sonner';
import { shareHouseholdInvite, canShare } from '@/utils/sharing';
import { showRealtimeToast } from '@/components/RealtimeToast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Animation variants for real-time updates
const itemAnimations = {
  initial: { opacity: 0, y: -20, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, scale: 0.95, height: 0, marginBottom: 0 }
};

const pulseAnimation = {
  animate: {
    scale: [1, 1.02, 1],
    backgroundColor: ['rgba(16, 185, 129, 0)', 'rgba(16, 185, 129, 0.1)', 'rgba(16, 185, 129, 0)']
  }
};

// ========== Member Card Component ==========
function MemberCard({ member, isOwner, currentUserId, onRemove, contribution }) {
  const isMe = member.user_id === currentUserId;
  const canRemove = isOwner && !isMe;
  
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
      <div className="flex items-center gap-3">
        {member.picture ? (
          <img src={member.picture} alt={member.name} className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold">
            {member.name?.charAt(0) || '?'}
          </div>
        )}
        <div>
          <p className="font-medium text-gray-900">
            {member.name} {isMe && <span className="text-gray-500">(you)</span>}
          </p>
          <p className="text-sm text-gray-500">{member.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {contribution !== undefined && (
          <span className="text-sm font-medium text-emerald-600">
            ${contribution.toLocaleString()}
          </span>
        )}
        <span className={`text-xs px-2 py-1 rounded-full ${
          member.role === 'owner' 
            ? 'bg-purple-100 text-purple-700' 
            : 'bg-gray-200 text-gray-600'
        }`}>
          {member.role === 'owner' ? '👑 Owner' : 'Member'}
        </span>
        {canRemove && (
          <button
            onClick={() => onRemove(member.user_id)}
            className="text-red-600 hover:text-red-800 text-sm"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

// ========== Household Card Component ==========
function HouseholdCard({ household, onSelect }) {
  return (
    <button
      onClick={() => onSelect(household)}
      className="w-full text-left p-5 bg-white rounded-2xl border-2 border-gray-200 hover:border-purple-400 transition-all shadow-sm hover:shadow-md"
      data-testid={`household-card-${household.id}`}
    >
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-bold text-gray-900">{household.name}</h3>
          <p className="text-sm text-gray-500 mt-1">
            {household.member_count} member{household.member_count !== 1 ? 's' : ''}
          </p>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full ${
          household.role === 'owner' 
            ? 'bg-purple-100 text-purple-700' 
            : 'bg-gray-200 text-gray-600'
        }`}>
          {household.role === 'owner' ? '👑 Owner' : 'Member'}
        </span>
      </div>
    </button>
  );
}

// ========== Contribution Card with Animation ==========
function ContributionCard({ contribution, isNew, onDelete, canDelete }) {
  return (
    <motion.div
      layout
      initial={isNew ? itemAnimations.initial : false}
      animate={isNew ? { ...itemAnimations.animate, backgroundColor: ['rgba(16, 185, 129, 0.2)', 'rgba(255, 255, 255, 1)'] } : itemAnimations.animate}
      exit={itemAnimations.exit}
      transition={{ duration: 0.4 }}
      className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
          <span className="text-emerald-600 text-lg">💵</span>
        </div>
        <div>
          <p className="font-medium text-gray-900">{contribution.contributor_name}'s Contribution</p>
          <p className="text-sm text-gray-500">{contribution.description}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-lg font-bold text-emerald-600">+${contribution.amount.toLocaleString()}</span>
        {canDelete && (
          <button
            onClick={() => onDelete(contribution.id)}
            className="text-gray-400 hover:text-red-500 transition-colors"
          >
            ✕
          </button>
        )}
      </div>
    </motion.div>
  );
}

// ========== Bill Card with Animation ==========
function BillCard({ bill, isNew, onTogglePaid, onDelete, onEdit, currentUserId }) {
  const [isPulsing, setIsPulsing] = useState(isNew);
  
  useEffect(() => {
    if (isNew) {
      const timer = setTimeout(() => setIsPulsing(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isNew]);

  return (
    <motion.div
      layout
      initial={isNew ? itemAnimations.initial : false}
      animate={isPulsing ? { ...itemAnimations.animate, backgroundColor: ['rgba(16, 185, 129, 0.2)', 'rgba(255, 255, 255, 1)'] } : itemAnimations.animate}
      exit={itemAnimations.exit}
      transition={{ duration: 0.4 }}
      className={`flex items-center justify-between p-4 bg-white rounded-xl border shadow-sm transition-all ${
        bill.paid_this_month ? 'border-emerald-300 bg-emerald-50/50' : 'border-gray-200'
      }`}
    >
      <div className="flex items-center gap-3">
        <motion.button
          onClick={() => onTogglePaid(bill.id, !bill.paid_this_month)}
          whileTap={{ scale: 0.9 }}
          className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
            bill.paid_this_month 
              ? 'bg-emerald-500 border-emerald-500 text-white' 
              : 'border-gray-300 hover:border-emerald-400'
          }`}
        >
          {bill.paid_this_month && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500 }}
            >
              ✓
            </motion.span>
          )}
        </motion.button>
        <div>
          <p className={`font-medium ${bill.paid_this_month ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
            {bill.name}
          </p>
          {bill.paid_by_name && (
            <p className="text-xs text-emerald-600">Paid by {bill.paid_by_name}</p>
          )}
          {bill.due_day && !bill.paid_this_month && (
            <p className="text-xs text-gray-500">Due: Day {bill.due_day}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-lg font-bold ${bill.paid_this_month ? 'text-gray-400' : 'text-red-600'}`}>
          ${bill.amount.toLocaleString()}
        </span>
        {/* Edit button */}
        <button
          onClick={() => onEdit(bill)}
          className="text-gray-400 hover:text-blue-500 transition-colors p-1"
          title="Edit bill"
        >
          ✏️
        </button>
        <button
          onClick={() => onDelete(bill.id)}
          className="text-gray-400 hover:text-red-500 transition-colors p-1"
        >
          ✕
        </button>
      </div>
    </motion.div>
  );
}

// ========== Expense Card with Animation ==========
function ExpenseCard({ expense, isNew, onDelete, canDelete }) {
  return (
    <motion.div
      layout
      initial={isNew ? itemAnimations.initial : false}
      animate={isNew ? { ...itemAnimations.animate, backgroundColor: ['rgba(251, 191, 36, 0.2)', 'rgba(255, 255, 255, 1)'] } : itemAnimations.animate}
      exit={itemAnimations.exit}
      transition={{ duration: 0.4 }}
      className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
    >
      <div className="flex items-center gap-3">
        <span className="text-lg">🧾</span>
        <div>
          <p className="font-medium text-gray-900">{expense.description}</p>
          <p className="text-xs text-gray-500">Added by {expense.added_by_name}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-medium text-orange-600">-${expense.amount.toLocaleString()}</span>
        {canDelete && (
          <button onClick={() => onDelete(expense.id)} className="text-gray-400 hover:text-red-500 text-sm">✕</button>
        )}
      </div>
    </motion.div>
  );
}

// ========== Main Household Component ==========
function Household() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken = searchParams.get('token');
  
  const [households, setHouseholds] = useState([]);
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showAddContribution, setShowAddContribution] = useState(false);
  const [showAddBill, setShowAddBill] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showEditBill, setShowEditBill] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [showUserNotFound, setShowUserNotFound] = useState(false);
  const [notFoundEmail, setNotFoundEmail] = useState('');
  const [newHouseholdName, setNewHouseholdName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [joiningHousehold, setJoiningHousehold] = useState(false);
  
  // Form states
  const [newContribution, setNewContribution] = useState({ amount: '', description: '' });
  const [newBill, setNewBill] = useState({ name: '', amount: '', due_day: '' });
  const [newExpense, setNewExpense] = useState({ description: '', amount: '', category: '' });
  
  // Track newly added items for animation
  const [newItems, setNewItems] = useState({ contributions: [], bills: [], expenses: [] });
  
  // WebSocket ref
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // ========== Load Current User ==========
  useEffect(() => {
    const loadUser = async () => {
      try {
        const response = await apiGet('/auth/me');
        if (response.ok) {
          const data = await response.json();
          setCurrentUser(data);
        }
      } catch (error) {
        console.error('Error loading user:', error);
      }
    };
    loadUser();
  }, []);

  // ========== Load Households ==========
  const loadHouseholds = useCallback(async () => {
    try {
      const response = await apiGet('/households');
      if (response.ok) {
        const data = await response.json();
        setHouseholds(data);
      }
    } catch (error) {
      console.error('Error loading households:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHouseholds();
  }, [loadHouseholds]);

  // ========== Handle Invite Token ==========
  useEffect(() => {
    if (inviteToken && !joiningHousehold) {
      handleJoinHousehold(inviteToken);
    }
  }, [inviteToken]);

  const handleJoinHousehold = async (token) => {
    setJoiningHousehold(true);
    try {
      const response = await apiPost(`/households/join/${token}`, {});
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        navigate('/app/household', { replace: true });
        loadHouseholds();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to join household');
      }
    } catch (error) {
      toast.error('Error joining household');
    } finally {
      setJoiningHousehold(false);
    }
  };

  // ========== Load Household Dashboard ==========
  const loadDashboard = useCallback(async (householdId) => {
    try {
      const response = await apiGet(`/households/${householdId}/dashboard`);
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  }, []);

  // ========== WebSocket Connection ==========
  const connectWebSocket = useCallback((householdId) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    const token = getToken();
    if (!token) return;

    const ws = new WebSocket(`${WS_URL}/ws/household/${householdId}?token=${token}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected to household:', householdId);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        handleWebSocketMessage(message);
      } catch (e) {
        console.error('WebSocket message parse error:', e);
      }
    };

    ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code);
      // Reconnect after 3 seconds if not intentional close
      if (event.code !== 1000 && selectedHousehold) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket(householdId);
        }, 3000);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    wsRef.current = ws;

    // Send ping every 30 seconds to keep connection alive
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [selectedHousehold]);

  // ========== Handle WebSocket Messages ==========
  const handleWebSocketMessage = useCallback((message) => {
    const { type, animate } = message;

    switch (type) {
      case 'contribution_added':
        setDashboardData(prev => {
          if (!prev) return prev;
          const newContributions = [message.contribution, ...prev.contributions];
          return {
            ...prev,
            contributions: newContributions,
            summary: {
              ...prev.summary,
              total_contributions: prev.summary.total_contributions + message.contribution.amount,
              leftover: prev.summary.leftover + message.contribution.amount
            }
          };
        });
        if (animate) {
          setNewItems(prev => ({ ...prev, contributions: [...prev.contributions, message.contribution.id] }));
          showRealtimeToast({
            type: 'household_contribution',
            title: 'New Contribution! 💵',
            message: `${message.contribution.contributor_name} added $${message.contribution.amount.toLocaleString()}`,
            subtitle: message.contribution.description,
            duration: 5000
          });
          setTimeout(() => {
            setNewItems(prev => ({ ...prev, contributions: prev.contributions.filter(id => id !== message.contribution.id) }));
          }, 2000);
        }
        break;

      case 'contribution_deleted':
        setDashboardData(prev => {
          if (!prev) return prev;
          const deleted = prev.contributions.find(c => c.id === message.contribution_id);
          return {
            ...prev,
            contributions: prev.contributions.filter(c => c.id !== message.contribution_id),
            summary: {
              ...prev.summary,
              total_contributions: prev.summary.total_contributions - (deleted?.amount || 0),
              leftover: prev.summary.leftover - (deleted?.amount || 0)
            }
          };
        });
        break;

      case 'bill_added':
        setDashboardData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            bills: [...prev.bills, message.bill],
            summary: {
              ...prev.summary,
              total_bills: prev.summary.total_bills + message.bill.amount,
              bills_unpaid: prev.summary.bills_unpaid + message.bill.amount,
              leftover: prev.summary.leftover - message.bill.amount
            }
          };
        });
        if (animate) {
          setNewItems(prev => ({ ...prev, bills: [...prev.bills, message.bill.id] }));
          showRealtimeToast({
            type: 'household_bill',
            title: 'New Shared Bill 📋',
            message: `${message.bill.name} - $${message.bill.amount.toLocaleString()}`,
            subtitle: `Due: ${message.bill.due_date || 'Not set'}`,
            duration: 5000
          });
          setTimeout(() => {
            setNewItems(prev => ({ ...prev, bills: prev.bills.filter(id => id !== message.bill.id) }));
          }, 2000);
        }
        break;

      case 'bill_updated':
        setDashboardData(prev => {
          if (!prev) return prev;
          const oldBill = prev.bills.find(b => b.id === message.bill_id);
          
          // Use full bill object if available, otherwise apply updates
          const newBillData = message.bill || { ...oldBill, ...message.updates };
          
          const updatedBills = prev.bills.map(b => 
            b.id === message.bill_id ? newBillData : b
          );
          
          // Recalculate totals
          const totalBills = updatedBills.reduce((sum, b) => sum + (b.amount || 0), 0);
          const billsPaid = updatedBills.filter(b => b.paid_this_month).reduce((sum, b) => sum + (b.amount || 0), 0);
          const billsUnpaid = updatedBills.filter(b => !b.paid_this_month).reduce((sum, b) => sum + (b.amount || 0), 0);
          
          // Calculate leftover adjustment
          const amountDiff = (newBillData.amount || 0) - (oldBill?.amount || 0);
          
          return {
            ...prev,
            bills: updatedBills,
            summary: { 
              ...prev.summary, 
              total_bills: totalBills,
              bills_paid: billsPaid, 
              bills_unpaid: billsUnpaid,
              leftover: prev.summary.leftover - amountDiff
            }
          };
        });
        if (animate) {
          if (message.updates?.paid_this_month !== undefined) {
            const paidBy = message.updates.paid_by_name || 'Someone';
            toast.info(message.updates.paid_this_month ? `${paidBy} marked a bill as paid` : 'A bill was marked unpaid');
          } else if (message.updates?.amount !== undefined) {
            toast.info(`Bill amount updated to $${message.updates.amount.toLocaleString()}`);
          }
        }
        break;

      case 'bill_deleted':
        setDashboardData(prev => {
          if (!prev) return prev;
          const deleted = prev.bills.find(b => b.id === message.bill_id);
          return {
            ...prev,
            bills: prev.bills.filter(b => b.id !== message.bill_id),
            summary: {
              ...prev.summary,
              total_bills: prev.summary.total_bills - (deleted?.amount || 0),
              leftover: prev.summary.leftover + (deleted?.amount || 0)
            }
          };
        });
        break;

      case 'expense_added':
        setDashboardData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            expenses: [message.expense, ...prev.expenses],
            summary: {
              ...prev.summary,
              total_expenses: prev.summary.total_expenses + message.expense.amount,
              leftover: prev.summary.leftover - message.expense.amount
            }
          };
        });
        if (animate) {
          setNewItems(prev => ({ ...prev, expenses: [...prev.expenses, message.expense.id] }));
          toast.info(`${message.expense.added_by_name} added an expense`);
          setTimeout(() => {
            setNewItems(prev => ({ ...prev, expenses: prev.expenses.filter(id => id !== message.expense.id) }));
          }, 2000);
        }
        break;

      case 'expense_deleted':
        setDashboardData(prev => {
          if (!prev) return prev;
          const deleted = prev.expenses.find(e => e.id === message.expense_id);
          return {
            ...prev,
            expenses: prev.expenses.filter(e => e.id !== message.expense_id),
            summary: {
              ...prev.summary,
              total_expenses: prev.summary.total_expenses - (deleted?.amount || 0),
              leftover: prev.summary.leftover + (deleted?.amount || 0)
            }
          };
        });
        break;

      case 'member_joined':
        toast.success(`${message.member.name} joined the household!`);
        loadDashboard(message.household_id);
        break;

      case 'member_removed':
      case 'member_left':
        toast.info('A member left the household');
        loadDashboard(message.household_id);
        break;

      case 'household_deleted':
        toast.error('This household has been deleted');
        setSelectedHousehold(null);
        setDashboardData(null);
        loadHouseholds();
        break;

      case 'budget_restored':
        toast.success(`Budget restored from "${message.month_name}"!`);
        loadDashboard(message.household_id || selectedHousehold?.id);
        break;

      case 'wrapped_up':
        toast.info(`Budget wrapped up and saved as "${message.month_name}"`);
        loadDashboard(message.household_id || selectedHousehold?.id);
        break;

      default:
        console.log('Unknown WebSocket message type:', type);
    }
  }, [loadDashboard, loadHouseholds, selectedHousehold]);

  // ========== Select Household ==========
  const handleSelectHousehold = async (household) => {
    setSelectedHousehold(household);
    await loadDashboard(household.id);
    connectWebSocket(household.id);
  };

  // ========== Cleanup WebSocket on unmount ==========
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // ========== Create Household ==========
  const createHousehold = async () => {
    if (!newHouseholdName.trim()) return;
    setSaving(true);
    try {
      const response = await apiPost('/households', { name: newHouseholdName });
      if (response.ok) {
        toast.success('Household created!');
        setNewHouseholdName('');
        setShowCreateForm(false);
        loadHouseholds();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create household');
      }
    } catch (error) {
      toast.error('Error creating household');
    } finally {
      setSaving(false);
    }
  };

  // ========== Invite Member ==========
  const inviteMember = async () => {
    if (!inviteEmail.trim() || !selectedHousehold) return;
    setSaving(true);
    try {
      const response = await apiPost(`/households/${selectedHousehold.id}/invite`, { email: inviteEmail });
      const data = await response.json();
      
      if (response.ok) {
        // Check if user was found on GuapGoals
        if (data.user_found === false || data.error_code === 'USER_NOT_FOUND') {
          // User not on GuapGoals - show native share modal
          setNotFoundEmail(inviteEmail);
          setShowUserNotFound(true);
          setInviteEmail('');
          setShowInviteForm(false);
        } else {
          // User found - invitation sent via in-app notification
          toast.success(`Invitation sent to ${inviteEmail}! They'll see it when they open GuapGoals.`);
          setInviteEmail('');
          setShowInviteForm(false);
          loadDashboard(selectedHousehold.id);
        }
      } else {
        toast.error(data.detail || 'Failed to send invitation');
      }
    } catch (error) {
      toast.error('Error sending invitation');
    } finally {
      setSaving(false);
    }
  };

  // ========== Add Contribution ==========
  const addContribution = async () => {
    if (!newContribution.amount || !selectedHousehold) return;
    setSaving(true);
    try {
      const response = await apiPost(`/households/${selectedHousehold.id}/contributions`, {
        amount: parseFloat(newContribution.amount),
        description: newContribution.description || undefined
      });
      if (response.ok) {
        const data = await response.json();
        // Add to local state immediately
        setDashboardData(prev => ({
          ...prev,
          contributions: [data, ...prev.contributions],
          summary: {
            ...prev.summary,
            total_contributions: prev.summary.total_contributions + data.amount,
            leftover: prev.summary.leftover + data.amount
          }
        }));
        setNewContribution({ amount: '', description: '' });
        setShowAddContribution(false);
        toast.success('Contribution added!');
      }
    } catch (error) {
      toast.error('Error adding contribution');
    } finally {
      setSaving(false);
    }
  };

  // ========== Add Bill ==========
  const addBill = async () => {
    if (!newBill.name || !newBill.amount || !selectedHousehold) return;
    setSaving(true);
    try {
      const response = await apiPost(`/households/${selectedHousehold.id}/bills`, {
        name: newBill.name,
        amount: parseFloat(newBill.amount),
        due_day: newBill.due_day ? parseInt(newBill.due_day) : undefined
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(prev => ({
          ...prev,
          bills: [...prev.bills, data],
          summary: {
            ...prev.summary,
            total_bills: prev.summary.total_bills + data.amount,
            bills_unpaid: prev.summary.bills_unpaid + data.amount,
            leftover: prev.summary.leftover - data.amount
          }
        }));
        setNewBill({ name: '', amount: '', due_day: '' });
        setShowAddBill(false);
        toast.success('Bill added!');
      }
    } catch (error) {
      toast.error('Error adding bill');
    } finally {
      setSaving(false);
    }
  };

  // ========== Toggle Bill Paid ==========
  const toggleBillPaid = async (billId, paid) => {
    if (!selectedHousehold) return;
    try {
      const response = await apiPut(`/households/${selectedHousehold.id}/bills/${billId}`, {
        paid_this_month: paid
      });
      if (response.ok) {
        // Update local state
        setDashboardData(prev => {
          const updatedBills = prev.bills.map(b => {
            if (b.id === billId) {
              return {
                ...b,
                paid_this_month: paid,
                paid_by: paid ? currentUser?.user_id : null,
                paid_by_name: paid ? currentUser?.name : null
              };
            }
            return b;
          });
          const billsPaid = updatedBills.filter(b => b.paid_this_month).reduce((sum, b) => sum + b.amount, 0);
          const billsUnpaid = updatedBills.filter(b => !b.paid_this_month).reduce((sum, b) => sum + b.amount, 0);
          return {
            ...prev,
            bills: updatedBills,
            summary: { ...prev.summary, bills_paid: billsPaid, bills_unpaid: billsUnpaid }
          };
        });
      }
    } catch (error) {
      toast.error('Error updating bill');
    }
  };

  // ========== Add Expense ==========
  const addExpense = async () => {
    if (!newExpense.description || !newExpense.amount || !selectedHousehold) return;
    setSaving(true);
    try {
      const response = await apiPost(`/households/${selectedHousehold.id}/expenses`, {
        description: newExpense.description,
        amount: parseFloat(newExpense.amount),
        category: newExpense.category || undefined
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(prev => ({
          ...prev,
          expenses: [data, ...prev.expenses],
          summary: {
            ...prev.summary,
            total_expenses: prev.summary.total_expenses + data.amount,
            leftover: prev.summary.leftover - data.amount
          }
        }));
        setNewExpense({ description: '', amount: '', category: '' });
        setShowAddExpense(false);
        toast.success('Expense added!');
      }
    } catch (error) {
      toast.error('Error adding expense');
    } finally {
      setSaving(false);
    }
  };

  // ========== Delete handlers ==========
  const deleteContribution = async (id) => {
    if (!selectedHousehold) return;
    try {
      const response = await apiDelete(`/households/${selectedHousehold.id}/contributions/${id}`);
      if (response.ok) {
        const deleted = dashboardData.contributions.find(c => c.id === id);
        setDashboardData(prev => ({
          ...prev,
          contributions: prev.contributions.filter(c => c.id !== id),
          summary: {
            ...prev.summary,
            total_contributions: prev.summary.total_contributions - (deleted?.amount || 0),
            leftover: prev.summary.leftover - (deleted?.amount || 0)
          }
        }));
      }
    } catch (error) {
      toast.error('Error deleting contribution');
    }
  };

  const deleteBill = async (id) => {
    if (!selectedHousehold) return;
    try {
      const response = await apiDelete(`/households/${selectedHousehold.id}/bills/${id}`);
      if (response.ok) {
        const deleted = dashboardData.bills.find(b => b.id === id);
        setDashboardData(prev => ({
          ...prev,
          bills: prev.bills.filter(b => b.id !== id),
          summary: {
            ...prev.summary,
            total_bills: prev.summary.total_bills - (deleted?.amount || 0),
            leftover: prev.summary.leftover + (deleted?.amount || 0)
          }
        }));
      }
    } catch (error) {
      toast.error('Error deleting bill');
    }
  };

  // ========== Edit Bill ==========
  const openEditBill = (bill) => {
    setEditingBill({
      id: bill.id,
      name: bill.name,
      amount: bill.amount.toString(),
      due_day: bill.due_day?.toString() || ''
    });
    setShowEditBill(true);
  };

  const saveEditBill = async () => {
    if (!editingBill || !selectedHousehold) return;
    setSaving(true);
    try {
      const response = await apiPut(`/households/${selectedHousehold.id}/bills/${editingBill.id}`, {
        name: editingBill.name,
        amount: parseFloat(editingBill.amount),
        due_day: editingBill.due_day ? parseInt(editingBill.due_day) : null
      });
      if (response.ok) {
        const updatedBill = await response.json();
        // Calculate the difference for summary update
        const oldBill = dashboardData.bills.find(b => b.id === editingBill.id);
        const amountDiff = (updatedBill.amount || parseFloat(editingBill.amount)) - (oldBill?.amount || 0);
        
        setDashboardData(prev => ({
          ...prev,
          bills: prev.bills.map(b => b.id === editingBill.id ? { ...b, ...updatedBill } : b),
          summary: {
            ...prev.summary,
            total_bills: prev.summary.total_bills + amountDiff,
            leftover: prev.summary.leftover - amountDiff
          }
        }));
        toast.success('Bill updated successfully');
        setShowEditBill(false);
        setEditingBill(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update bill');
      }
    } catch (error) {
      toast.error('Error updating bill');
    } finally {
      setSaving(false);
    }
  };

  const deleteExpense = async (id) => {
    if (!selectedHousehold) return;
    try {
      const response = await apiDelete(`/households/${selectedHousehold.id}/expenses/${id}`);
      if (response.ok) {
        const deleted = dashboardData.expenses.find(e => e.id === id);
        setDashboardData(prev => ({
          ...prev,
          expenses: prev.expenses.filter(e => e.id !== id),
          summary: {
            ...prev.summary,
            total_expenses: prev.summary.total_expenses - (deleted?.amount || 0),
            leftover: prev.summary.leftover + (deleted?.amount || 0)
          }
        }));
      }
    } catch (error) {
      toast.error('Error deleting expense');
    }
  };

  const removeMember = async (userId) => {
    if (!selectedHousehold || !window.confirm('Remove this member from the household?')) return;
    try {
      const response = await apiDelete(`/households/${selectedHousehold.id}/members/${userId}`);
      if (response.ok) {
        toast.success('Member removed');
        loadDashboard(selectedHousehold.id);
      }
    } catch (error) {
      toast.error('Error removing member');
    }
  };

  const cancelInvite = async (inviteId) => {
    if (!selectedHousehold) return;
    try {
      const response = await apiDelete(`/households/${selectedHousehold.id}/invite/${inviteId}`);
      if (response.ok) {
        toast.success('Invitation cancelled');
        loadDashboard(selectedHousehold.id);
      }
    } catch (error) {
      toast.error('Error cancelling invitation');
    }
  };

  // ========== Loading State ==========
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // ========== Household Dashboard View ==========
  if (selectedHousehold && dashboardData) {
    const { household, summary, contributions, bills, expenses, contributions_by_member } = dashboardData;
    const isOwner = household.members?.find(m => m.user_id === currentUser?.user_id)?.role === 'owner';

    // Wrap Up Handler
    const handleWrapUp = async () => {
      if (!window.confirm(
        '⚠️ Wrap Up & Start Fresh?\n\n' +
        'This will:\n' +
        '• Save a snapshot of this month\'s contributions and bills\n' +
        '• Clear all contributions for a fresh start\n' +
        '• Reset all bill paid statuses\n\n' +
        'The snapshot will be saved to Budget History.'
      )) {
        return;
      }
      
      setSaving(true);
      try {
        const response = await apiPost(`/households/${selectedHousehold.id}/wrap-up`, {});
        if (response.ok) {
          const data = await response.json();
          toast.success(`Month saved as "${data.saved_as}"!`);
          // Reload dashboard to show cleared state
          await loadDashboard(selectedHousehold.id);
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to wrap up');
        }
      } catch (error) {
        toast.error('Error wrapping up month');
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="space-y-6 pb-20">
        {/* Wrap Up Banner */}
        <div className="bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <span className="text-2xl">📦</span>
            <div>
              <p className="font-semibold">End of Month?</p>
              <p className="text-sm text-amber-100">Save this month's data and start fresh</p>
            </div>
          </div>
          <button
            onClick={handleWrapUp}
            disabled={saving}
            className="px-5 py-2 bg-white text-amber-600 rounded-lg font-semibold hover:bg-amber-50 transition-colors disabled:opacity-50"
            data-testid="household-wrap-up-btn"
          >
            {saving ? 'Saving...' : 'Wrap Up & Start Fresh'}
          </button>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => { setSelectedHousehold(null); setDashboardData(null); }}
              className="text-gray-500 hover:text-gray-700 text-sm mb-2 flex items-center gap-1"
            >
              ← Back to Households
            </button>
            <h1 className="text-2xl font-bold text-gray-900">{household.name}</h1>
            <p className="text-gray-500">{household.member_count} members</p>
          </div>
          <Link
            to="/app/dashboard"
            className="text-sm text-purple-600 hover:text-purple-800 font-medium"
          >
            Personal Budget →
          </Link>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-4 text-white">
            <p className="text-emerald-100 text-sm">Total Pool</p>
            <p className="text-2xl font-bold">${summary.total_contributions.toLocaleString()}</p>
          </div>
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-4 text-white">
            <p className="text-red-100 text-sm">Bills</p>
            <p className="text-2xl font-bold">${summary.total_bills.toLocaleString()}</p>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-4 text-white">
            <p className="text-orange-100 text-sm">Expenses</p>
            <p className="text-2xl font-bold">${summary.total_expenses.toLocaleString()}</p>
          </div>
          <div className={`rounded-2xl p-4 text-white ${summary.leftover >= 0 ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-gray-600 to-gray-700'}`}>
            <p className="text-blue-100 text-sm">Leftover</p>
            <p className="text-2xl font-bold">${summary.leftover.toLocaleString()}</p>
          </div>
        </div>

        {/* Contributions Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">💵 Contributions</h2>
            <button
              onClick={() => setShowAddContribution(true)}
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors"
            >
              + Add Contribution
            </button>
          </div>

          {/* Contributions by Member Breakdown */}
          {contributions_by_member && contributions_by_member.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {contributions_by_member.map(member => (
                <div key={member.user_id} className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-sm text-emerald-800 font-medium">{member.name}'s Contribution</p>
                  <p className="text-xl font-bold text-emerald-600">${member.total.toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}

          <AnimatePresence mode="popLayout">
            <div className="space-y-3">
              {contributions.map(c => (
                <ContributionCard
                  key={c.id}
                  contribution={c}
                  isNew={newItems.contributions.includes(c.id)}
                  onDelete={deleteContribution}
                  canDelete={c.user_id === currentUser?.user_id || isOwner}
                />
              ))}
            </div>
          </AnimatePresence>

          {contributions.length === 0 && (
            <p className="text-gray-500 text-center py-8">No contributions yet. Add one to get started!</p>
          )}
        </div>

        {/* Shared Bills Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">📋 Shared Bills</h2>
            <button
              onClick={() => setShowAddBill(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
            >
              + Add Bill
            </button>
          </div>

          {/* Bills Progress */}
          {summary.total_bills > 0 && (
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Paid: ${summary.bills_paid.toLocaleString()}</span>
                <span className="text-gray-600">Remaining: ${summary.bills_unpaid.toLocaleString()}</span>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-emerald-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${(summary.bills_paid / summary.total_bills) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          )}

          <AnimatePresence mode="popLayout">
            <div className="space-y-3">
              {bills.map(b => (
                <BillCard
                  key={b.id}
                  bill={b}
                  isNew={newItems.bills.includes(b.id)}
                  onTogglePaid={toggleBillPaid}
                  onDelete={deleteBill}
                  onEdit={openEditBill}
                  currentUserId={currentUser?.user_id}
                />
              ))}
            </div>
          </AnimatePresence>

          {bills.length === 0 && (
            <p className="text-gray-500 text-center py-8">No shared bills yet.</p>
          )}
        </div>

        {/* Quick Expenses Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">🧾 Shared Expenses</h2>
            <button
              onClick={() => setShowAddExpense(true)}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
            >
              + Add Expense
            </button>
          </div>

          <AnimatePresence mode="popLayout">
            <div className="space-y-2">
              {expenses.slice(0, 10).map(e => (
                <ExpenseCard
                  key={e.id}
                  expense={e}
                  isNew={newItems.expenses.includes(e.id)}
                  onDelete={deleteExpense}
                  canDelete={e.user_id === currentUser?.user_id || isOwner}
                />
              ))}
            </div>
          </AnimatePresence>

          {expenses.length === 0 && (
            <p className="text-gray-500 text-center py-8">No shared expenses logged.</p>
          )}
        </div>

        {/* Members Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">👥 Members ({household.member_count}/6)</h2>
            {isOwner && household.member_count < 6 && (
              <button
                onClick={() => setShowInviteForm(true)}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors"
              >
                + Invite Member
              </button>
            )}
          </div>

          <div className="space-y-3">
            {household.members?.map(member => {
              const memberContrib = contributions_by_member?.find(c => c.user_id === member.user_id);
              return (
                <MemberCard
                  key={member.user_id}
                  member={member}
                  isOwner={isOwner}
                  currentUserId={currentUser?.user_id}
                  onRemove={removeMember}
                  contribution={memberContrib?.total}
                />
              );
            })}
          </div>

          {/* Pending Invites */}
          {isOwner && dashboardData.pending_invites?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Pending Invitations</h3>
              {dashboardData.pending_invites.map(invite => (
                <div key={invite.id} className="flex items-center justify-between py-2 px-3 bg-yellow-50 rounded-lg mb-2">
                  <span className="text-sm text-yellow-800">{invite.receiver_email || invite.email}</span>
                  <button
                    onClick={() => cancelInvite(invite.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal Forms */}
        <AnimatePresence>
          {showAddContribution && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={() => setShowAddContribution(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="bg-white rounded-2xl p-6 w-full max-w-md"
                onClick={e => e.stopPropagation()}
              >
                <h3 className="text-lg font-bold mb-4">Add Your Contribution</h3>
                <input
                  type="number"
                  placeholder="Amount ($)"
                  value={newContribution.amount}
                  onChange={e => setNewContribution(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-3 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={newContribution.description}
                  onChange={e => setNewContribution(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-4 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowAddContribution(false)}
                    className="flex-1 py-3 border border-gray-300 rounded-xl font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addContribution}
                    disabled={saving || !newContribution.amount}
                    className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-medium disabled:opacity-50"
                  >
                    {saving ? 'Adding...' : 'Add Contribution'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {showAddBill && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={() => setShowAddBill(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="bg-white rounded-2xl p-6 w-full max-w-md"
                onClick={e => e.stopPropagation()}
              >
                <h3 className="text-lg font-bold mb-4">Add Shared Bill</h3>
                <input
                  type="text"
                  placeholder="Bill name (e.g., Rent, Electric)"
                  value={newBill.name}
                  onChange={e => setNewBill(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="number"
                  placeholder="Amount ($)"
                  value={newBill.amount}
                  onChange={e => setNewBill(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="number"
                  placeholder="Due day (1-31, optional)"
                  min="1"
                  max="31"
                  value={newBill.due_day}
                  onChange={e => setNewBill(prev => ({ ...prev, due_day: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex gap-3">
                  <button onClick={() => setShowAddBill(false)} className="flex-1 py-3 border border-gray-300 rounded-xl font-medium">Cancel</button>
                  <button onClick={addBill} disabled={saving || !newBill.name || !newBill.amount} className="flex-1 py-3 bg-blue-500 text-white rounded-xl font-medium disabled:opacity-50">
                    {saving ? 'Adding...' : 'Add Bill'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Edit Bill Modal */}
          {showEditBill && editingBill && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={() => { setShowEditBill(false); setEditingBill(null); }}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="bg-white rounded-2xl p-6 w-full max-w-md"
                onClick={e => e.stopPropagation()}
              >
                <h3 className="text-lg font-bold mb-4">✏️ Edit Bill</h3>
                <p className="text-sm text-gray-500 mb-4">Update the bill amount for this month's variable expenses.</p>
                <input
                  type="text"
                  placeholder="Bill name"
                  value={editingBill.name}
                  onChange={e => setEditingBill(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="number"
                  placeholder="Amount ($)"
                  value={editingBill.amount}
                  onChange={e => setEditingBill(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="number"
                  placeholder="Due day (1-31, optional)"
                  min="1"
                  max="31"
                  value={editingBill.due_day}
                  onChange={e => setEditingBill(prev => ({ ...prev, due_day: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex gap-3">
                  <button onClick={() => { setShowEditBill(false); setEditingBill(null); }} className="flex-1 py-3 border border-gray-300 rounded-xl font-medium">Cancel</button>
                  <button onClick={saveEditBill} disabled={saving || !editingBill.name || !editingBill.amount} className="flex-1 py-3 bg-blue-500 text-white rounded-xl font-medium disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {showAddExpense && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={() => setShowAddExpense(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="bg-white rounded-2xl p-6 w-full max-w-md"
                onClick={e => e.stopPropagation()}
              >
                <h3 className="text-lg font-bold mb-4">Add Shared Expense</h3>
                <input
                  type="text"
                  placeholder="What was it for? (e.g., Groceries)"
                  value={newExpense.description}
                  onChange={e => setNewExpense(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-3 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <input
                  type="number"
                  placeholder="Amount ($)"
                  value={newExpense.amount}
                  onChange={e => setNewExpense(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-4 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <div className="flex gap-3">
                  <button onClick={() => setShowAddExpense(false)} className="flex-1 py-3 border border-gray-300 rounded-xl font-medium">Cancel</button>
                  <button onClick={addExpense} disabled={saving || !newExpense.description || !newExpense.amount} className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-medium disabled:opacity-50">
                    {saving ? 'Adding...' : 'Add Expense'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {showInviteForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={() => setShowInviteForm(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="bg-white rounded-2xl p-6 w-full max-w-md"
                onClick={e => e.stopPropagation()}
              >
                <h3 className="text-lg font-bold mb-4">Invite Member</h3>
                <p className="text-gray-600 text-sm mb-4">They'll receive an email with a link to join your household budget.</p>
                <input
                  type="email"
                  placeholder="Email address"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-4 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <div className="flex gap-3">
                  <button onClick={() => setShowInviteForm(false)} className="flex-1 py-3 border border-gray-300 rounded-xl font-medium">Cancel</button>
                  <button onClick={inviteMember} disabled={saving || !inviteEmail} className="flex-1 py-3 bg-purple-500 text-white rounded-xl font-medium disabled:opacity-50">
                    {saving ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ========== Household List View ==========
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="household-title">👨‍👩‍👧‍👦 Household Budget</h1>
          <p className="text-gray-500">Share budgets with family or roommates</p>
        </div>
        <Link
          to="/app/dashboard"
          className="text-sm text-purple-600 hover:text-purple-800 font-medium"
        >
          Personal Budget →
        </Link>
      </div>

      {/* Privacy Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <span className="text-blue-600 text-xl">🔒</span>
          <div>
            <h3 className="font-medium text-blue-900">Your Personal Budget is Private</h3>
            <p className="text-sm text-blue-800 mt-1">
              Household budgets are completely separate from your personal budget. 
              Your personal income, bills, and expenses are never shared with household members.
            </p>
          </div>
        </div>
      </div>

      {/* Households List */}
      {households.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-6xl mb-4">🏠</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Household Budgets Yet</h3>
          <p className="text-gray-600 mb-6">Create a household to share bills and expenses with family or roommates.</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors"
            data-testid="create-household-btn"
          >
            Create Household Budget
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4">
            {households.map(h => (
              <HouseholdCard key={h.id} household={h} onSelect={handleSelectHousehold} />
            ))}
          </div>
          
          <button
            onClick={() => setShowCreateForm(true)}
            className="w-full py-4 border-2 border-dashed border-gray-300 rounded-2xl text-gray-500 hover:border-purple-400 hover:text-purple-600 transition-colors font-medium"
          >
            + Create Another Household
          </button>
        </div>
      )}

      {/* Create Form Modal */}
      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowCreateForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-4">Create Household Budget</h3>
              <input
                type="text"
                placeholder="Household name (e.g., The Smiths, Apt 4B)"
                value={newHouseholdName}
                onChange={e => setNewHouseholdName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl mb-4 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="household-name-input"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 py-3 border border-gray-300 rounded-xl font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={createHousehold}
                  disabled={saving || !newHouseholdName.trim()}
                  className="flex-1 py-3 bg-purple-600 text-white rounded-xl font-medium disabled:opacity-50"
                  data-testid="confirm-create-household-btn"
                >
                  {saving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* User Not Found Modal - Shows when invitee isn't on GuapGoals */}
      <AnimatePresence>
        {showUserNotFound && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
            onClick={() => setShowUserNotFound(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-3xl">🔍</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">User Not Found</h2>
              </div>

              {/* Message */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6">
                <p className="text-amber-800 text-center">
                  Looks like <span className="font-semibold">{notFoundEmail}</span> isn't on GuapGoals yet!
                </p>
              </div>

              <p className="text-sm text-gray-500 text-center mb-6">
                Would you like to invite them to download the app and join your household?
              </p>

              {/* Share Buttons */}
              <div className="space-y-3">
                {canShare() && (
                  <button
                    onClick={async () => {
                      const share = await shareHouseholdInvite(selectedHousehold?.name, notFoundEmail);
                      await share.shareNative();
                      setShowUserNotFound(false);
                    }}
                    className="w-full py-3 px-4 bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-all flex items-center justify-center gap-2"
                  >
                    <span>📤</span> Share Invite Link
                  </button>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      const share = await shareHouseholdInvite(selectedHousehold?.name, notFoundEmail);
                      share.sendText();
                      setShowUserNotFound(false);
                    }}
                    className="flex-1 py-3 px-4 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
                    data-testid="send-text-invite"
                  >
                    <span>💬</span> Send Text
                  </button>
                  <button
                    onClick={async () => {
                      const share = await shareHouseholdInvite(selectedHousehold?.name, notFoundEmail);
                      share.sendEmail();
                      setShowUserNotFound(false);
                    }}
                    className="flex-1 py-3 px-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                    data-testid="send-email-invite"
                  >
                    <span>📧</span> Send Email
                  </button>
                </div>
              </div>

              {/* Cancel button */}
              <button
                onClick={() => setShowUserNotFound(false)}
                className="w-full mt-4 py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Household;
